import { sendDongDongMessage } from '@/api/newApi/dongDong';
import { isGuest } from '@/util/commonUtils';
import ChineseScanner from '@/util/scanChineseText';

const TRANSLATION_INSPECTOR_DISABLED_STORAGE_KEY = 'wisp_translation_inspector_disabled';
const TRANSLATION_INSPECTOR_DISABLED_QUERY_KEY = 'translationInspectorDisabled';
const LANG_MANAGE_URL = 'https://wimp-pre.jd.com/#/langs/list';
const DONG_DONG_ROBOT_TYPE = 6;
const DONG_DONG_RECEIVE_ERPS = [
  'wuzhonghai1',
  'weiyanna',
  'jinyu.levy',
  'jiaoding1',
  'wangpeng965',
  'yuyang63',
  'fujiebin1',
  'jiangbo.2026',
  'machao82',
  'zhangyuhong47'
];
const SCANNER_START_DELAY = 2000;
const ROUTE_REPORT_DELAY = 2000;
const TRANSLATION_INSPECTION_LOG_PREFIX = '[Translation Inspection]';

interface TranslationInspectionResult {
  success: boolean;
  skipped: boolean;
  count: number;
  texts: string[];
  message?: string;
}

let scannerInitialized = false;
let scannerStartTimer: number | null = null;
let reportTimer: number | null = null;

function getTranslationInspectionGate() {
  const searchParams = new URLSearchParams(window.location.search);
  const disabledByUser =
    searchParams.get(TRANSLATION_INSPECTOR_DISABLED_QUERY_KEY) === '1' ||
    window.localStorage.getItem(TRANSLATION_INSPECTOR_DISABLED_STORAGE_KEY) === '1';
  const isGuestUser = isGuest();

  return {
    // 生产默认开启漏翻巡检；游客站没有登录态，咚咚消息接口会 403 并触发全局 403 页，需直接跳过。
    enabled: !disabledByUser && !isGuestUser,
    disabledByUser,
    isGuestUser,
  };
}

function getCurrentLanguage() {
  return (
    window.localStorage.getItem('_language') ||
    document.documentElement.getAttribute('lang') ||
    navigator.language ||
    'zh'
  );
}

function buildInspectionMessage(pageUrl: string, texts: string[]): string {
  return JSON.stringify(
    `在wisp系统当前页面路径 ${pageUrl} 发现以下中文【${texts.join(
      ',',
    )}】 未翻译,请尽快处理!!!。处理地址 ${LANG_MANAGE_URL}`,
  );
}

async function reportChineseTextsForCurrentPage(): Promise<TranslationInspectionResult> {
  const inspectionGate = getTranslationInspectionGate();
  const currentLanguage = getCurrentLanguage();

  if (!inspectionGate.enabled || currentLanguage === 'zh') {
    console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} report skipped`, {
      ...inspectionGate,
      currentLanguage,
      pageUrl: window.location.href,
    });
    return {
      success: true,
      skipped: true,
      count: 0,
      texts: [],
    };
  }

  const texts = ChineseScanner.getNewAndSync();
  console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} collect result`, {
    count: texts.length,
    texts,
    pageUrl: window.location.href,
  });

  if (!texts.length) {
    return {
      success: true,
      skipped: true,
      count: 0,
      texts,
    };
  }

  const traceId = String(Math.random());
  const inspectionMessage = buildInspectionMessage(window.location.href, texts);
  // 这条链路用于非中文语言环境巡检页面漏翻文案，traceId 与接口层打印保持一致，便于串起调度和请求。
  console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} send dongDong message`, {
    traceId,
    robotType: DONG_DONG_ROBOT_TYPE,
    receiveErps: DONG_DONG_RECEIVE_ERPS,
    count: texts.length,
    pageUrl: window.location.href,
    texts,
  });

  const response = await sendDongDongMessage({
    traceId,
    robotType: DONG_DONG_ROBOT_TYPE,
    receiveErps: DONG_DONG_RECEIVE_ERPS,
    message: inspectionMessage,
  });

  const sent = Boolean(response?.success && (response?.data === true || response?.value === true));
  console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} dongDong send result`, {
    traceId,
    sent,
    response,
  });

  return {
    success: sent,
    skipped: false,
    count: texts.length,
    texts,
    message: response?.msg || response?.message,
  };
}

export function scheduleChineseTextReport(delay = ROUTE_REPORT_DELAY) {
  const inspectionGate = getTranslationInspectionGate();

  if (!inspectionGate.enabled) {
    if (reportTimer) {
      window.clearTimeout(reportTimer);
      reportTimer = null;
    }
    console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} report schedule skipped`, {
      ...inspectionGate,
      pageUrl: window.location.href,
    });
    return;
  }

  if (reportTimer) {
    window.clearTimeout(reportTimer);
    console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} pending report rescheduled`);
  }

  console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} report scheduled`, {
    delay,
    pageUrl: window.location.href,
  });

  reportTimer = window.setTimeout(() => {
    reportTimer = null;
    void reportChineseTextsForCurrentPage()
      .then(result => {
        if (!result.skipped && result.success) {
          console.log('[Chinese Scanner] Report success:', result.texts);
        }
      })
      .catch(error => {
        console.error('[Chinese Scanner] Report failed:', error);
      });
  }, delay);
}

export function initChineseTextInspection() {
  const inspectionGate = getTranslationInspectionGate();

  if (scannerInitialized || !inspectionGate.enabled) {
    console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} init skipped`, {
      scannerInitialized,
      ...inspectionGate,
      pageUrl: window.location.href,
    });
    return () => undefined;
  }

  scannerInitialized = true;
  console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} init scheduled`, {
    delay: SCANNER_START_DELAY,
    pageUrl: window.location.href,
  });

  scannerStartTimer = window.setTimeout(() => {
    console.log(`${TRANSLATION_INSPECTION_LOG_PREFIX} scanner starting`, {
      pageUrl: window.location.href,
    });
    ChineseScanner.start();
    (window as any).__ChineseScanner = ChineseScanner;
    scheduleChineseTextReport(0);
  }, SCANNER_START_DELAY);

  return () => {
    if (scannerStartTimer) {
      window.clearTimeout(scannerStartTimer);
      scannerStartTimer = null;
    }
    if (reportTimer) {
      window.clearTimeout(reportTimer);
      reportTimer = null;
    }
    ChineseScanner.stop();
    scannerInitialized = false;
  };
}
